/**
 * Types du domaine, partagés par le client et les Pages Functions.
 *
 * Ce module — comme tout `shared/` — est du code pur : aucune dépendance
 * runtime (pas de fetch, pas d'IndexedDB, pas de binding D1, pas de React).
 * C'est ce qui permet de tester la logique de fusion en mémoire.
 */

export type Platform = 'android' | 'ios' | 'other';

/** Un foyer : un espace partagé, sans compte utilisateur. */
export interface Household {
  id: string;
  name: string;
  created_at: number;
}

export interface List {
  id: string;
  household_id: string;
  name: string;
  /** Indexation fractionnaire, cf. shared/position.ts */
  position: number;
  is_default: boolean;
  /** Horodatage métier ET curseur de synchronisation. Assigné par le serveur. */
  updated_at: number;
  created_at: number;
  deleted_at: number | null;
}

export interface Item {
  id: string;
  household_id: string;
  list_id: string;
  label: string;
  quantity: string | null;
  is_checked: boolean;
  position: number;

  created_at: number;
  created_by_device: string | null;
  updated_at: number;
  checked_at: number | null;
  checked_by_device: string | null;
  deleted_at: number | null;
}

export interface Device {
  id: string;
  household_id: string;
  label: string;
  platform: Platform;
  created_at: number;
  updated_at: number;
}

/** Charge utile d'un delta de synchronisation. */
export interface SyncDelta {
  /** Curseur à renvoyer au prochain appel. Avance même si le delta est vide. */
  cursor: number;
  lists: List[];
  items: Item[];
  devices: Device[];
}

// ---------------------------------------------------------------------------
// Conversion base ⇄ domaine
//
// D1/SQLite n'a pas de type booléen : les booléens sont stockés en INTEGER 0/1.
// La conversion est centralisée ici pour qu'aucun `!!row.is_checked` ne traîne
// dans le code applicatif.
// ---------------------------------------------------------------------------

export interface ListRow {
  id: string;
  household_id: string;
  name: string;
  position: number;
  is_default: number;
  updated_at: number;
  created_at: number;
  deleted_at: number | null;
}

export interface ItemRow {
  id: string;
  household_id: string;
  list_id: string;
  label: string;
  quantity: string | null;
  is_checked: number;
  position: number;
  created_at: number;
  created_by_device: string | null;
  updated_at: number;
  checked_at: number | null;
  checked_by_device: string | null;
  deleted_at: number | null;
}

export interface DeviceRow {
  id: string;
  household_id: string;
  label: string;
  platform: string;
  created_at: number;
  updated_at: number;
}

export function listFromRow(row: ListRow): List {
  return { ...row, is_default: row.is_default === 1 };
}

export function itemFromRow(row: ItemRow): Item {
  return { ...row, is_checked: row.is_checked === 1 };
}

export function deviceFromRow(row: DeviceRow): Device {
  return { ...row, platform: normalisePlatform(row.platform) };
}

export function normalisePlatform(value: string): Platform {
  return value === 'android' || value === 'ios' ? value : 'other';
}

/** Un article est supprimé si et seulement si deleted_at est renseigné. */
export function isDeleted(row: { deleted_at: number | null }): boolean {
  return row.deleted_at !== null;
}
